/**
 * Fee-adjusted top-of-book pricing engine for sports markets.
 *
 * Per-contract fee formula and helpers. Fee *rates* are no longer hardcoded —
 * they come from hydra's per-series fee schedule via `services/hydraFees`.
 * Pure formula functions (perContractFeeCents, applyTakerFee) take an
 * explicit rate so they remain unit-testable; the lookup functions resolve
 * a ticker to its rate and return `null` when the schedule isn't loaded yet.
 */

import type { BookLevel, TeamSide, VenueGameBooks } from '@/types';
import { feeFor } from '@/services/hydraFees';

/**
 * Per-contract taker fee in cents — canonical fee formula (T-006).
 * fee = feeRate * P * (1-P) * 100   where P = priceCents / 100
 *
 * Result is rounded UP to a centicent ($0.0001 = 0.01¢) per Kalshi's fee
 * rounding spec (https://docs.kalshi.com/getting_started/fee_rounding) so
 * pre-trade estimates match Kalshi's per-fill charge before any cross-fill
 * accumulator rebate. The accumulator/rebate logic is settlement-side and
 * shows up in the realized `fee_cost` we'll reconcile against post-fill.
 *
 * `feeRate` is the effective rate (already × series multiplier when sourced
 * from hydra). Polymarket pre-multiplied → caller passes 0.
 */
export function perContractFeeCents(priceCents: number, feeRate: number): number {
  const P = priceCents / 100;
  const rawCents = feeRate * P * (1 - P) * 100;
  // Boundary prices (0/100) and rate=0 give a clean 0; short-circuit before
  // ceiling so the epsilon-shifted Math.ceil doesn't return -0.
  if (rawCents <= 0) return 0;
  // Centicent = 0.01¢ → multiplier 100 against cents. The 1e-9 epsilon
  // absorbs FP fuzz at exactly-aligned values: 0.07 × 0.5 × 0.5 × 100
  // evaluates to 1.7500000000000002 in JS, which would falsely tip ceil to
  // 1.76 without the subtraction. 1e-9 is far above the mantissa noise at
  // these scales and far below the 0.01¢ tick we round to.
  return Math.ceil(rawCents * 100 - 1e-9) / 100;
}

export function bidToAsk(bidCents: number | null | undefined): number | null {
  if (bidCents === null || bidCents === undefined) return null;
  const v = Number(bidCents);
  if (!Number.isFinite(v)) return null;
  return 100 - v;
}

/**
 * Fee computed on a notional `contracts` (default 1000) then spread per contract.
 */
export function applyTakerFee(
  priceCents: number | null,
  feeRate: number,
  contracts = 1000
): number | null {
  if (priceCents === null) return null;
  const p = Number(priceCents);
  if (!Number.isFinite(p) || p <= 0 || p >= 100) return p;

  const feeTotalCents = Math.ceil(perContractFeeCents(p, feeRate) * contracts);
  const feePerContractCents = feeTotalCents / contracts;
  return p + feePerContractCents;
}

/**
 * Per-contract hydra fee estimate in cents at centicent precision:
 *
 *   perContractFeeCents(P, rate) × count
 *
 * Sub-cent precision, no ceil-to-cent. Used for the per-contract
 * effective-price display (ladder / pill odds), where a single contract's
 * fractional-cent fee should be added as-is rather than rounded up to the
 * 1¢ per-fill billing minimum. For an order-TOTAL estimate that matches
 * Kalshi's ticket, use {@link estimateAggregateFeeCents} instead.
 *
 * Returns 0 when the rate is 0 or null (Polymarket, unknown series).
 */
export function estimateOrderFeeCents(args: {
  priceCents: number;
  count: number;
  feeRate: number | null;
}): number {
  const { priceCents, count, feeRate } = args;
  if (!feeRate || count <= 0 || priceCents <= 0 || priceCents >= 100) return 0;
  return perContractFeeCents(priceCents, feeRate) * count;
}

/**
 * Pre-trade fee estimate for a single resting order, in **whole cents**,
 * matching Kalshi's per-fill (per-execution) rounding spec
 * (https://docs.kalshi.com/getting_started/fee_rounding):
 *
 *   fee = ceil( feeRate × P × (1−P) × 100 × count )   (rounded up to 1¢)
 *
 * Kalshi computes the quadratic fee on the *whole executed quantity* and
 * rounds the result up to the next cent — it does NOT round per contract.
 * {@link perContractFeeCents} rounds up to a centicent *per contract*,
 * which over-charges on large orders (43¢/contract × 20 000 = $86.00 vs
 * Kalshi's $85.26 on a 42¢ / 20 000 maker fill). Use this aggregate path
 * for any order-/position-total fee estimate so the displayed total matches
 * Kalshi's own ticket; {@link estimateOrderFeeCents} keeps the centicent
 * per-contract value for the effective-price-per-contract odds display.
 *
 * This IS the estimate, full stop. There is no multi-fill premium: Kalshi
 * charges the quadratic fee on the executed quantity regardless of how many
 * executions it took, so a single-execution ceil is the correct prediction.
 * (An earlier site-wide calibration coefficient that scaled this up for
 * assumed fill-splitting was removed once that premise was found false.)
 *
 * Returns 0 when the rate is 0 or null (Polymarket, unknown series) or the
 * inputs are degenerate (boundary price, non-positive count).
 */
export function estimateAggregateFeeCents(args: {
  priceCents: number;
  count: number;
  feeRate: number | null;
}): number {
  const { priceCents, count, feeRate } = args;
  if (!feeRate || count <= 0 || priceCents <= 0 || priceCents >= 100) return 0;
  const P = priceCents / 100;
  const rawCents = feeRate * P * (1 - P) * 100 * count;
  if (rawCents <= 0) return 0;
  // Round the whole-fill fee up to the next cent (Kalshi bills to 1¢ per
  // execution). The 1e-9 epsilon absorbs FP fuzz at exactly-aligned values
  // — e.g. 0.0175 × 0.42 × 0.58 × 100 × 20000 evaluates just above 8526 in
  // JS, which would falsely tip ceil to 8527 without the subtraction. The
  // epsilon is far below the 1¢ tick and far above mantissa noise here.
  return Math.ceil(rawCents - 1e-9);
}

/** Display-only effective price for American-odds labels. Keeps the raw cents
 *  used for order placement untouched and adds only the per-contract fee
 *  estimate needed to express the all-in odds. The Kalshi Market Maker
 *  Program rebate is applied as a fraction (0..1) of the maker fee when
 *  `makerRebatePct` is supplied — callers pass 0 unless the org is enrolled
 *  and the MM Rebate toggle is on. See lib/pricing/makerRebate. */
export function effectivePriceForFeeAdjustedOdds(
  priceCents: number,
  feeAdjusted: boolean,
  feeRate: number | null,
  makerRebatePct = 0,
): number {
  if (!feeAdjusted) return priceCents;
  const feeCents = estimateOrderFeeCents({ priceCents, count: 1, feeRate });
  const netFeeCents =
    makerRebatePct > 0 ? feeCents * (1 - Math.min(makerRebatePct, 1)) : feeCents;
  const effective = priceCents + netFeeCents;
  return effective > 0 ? effective : 0.01;
}

/**
 * Look up the per-series taker rate for a Kalshi ticker.
 *
 * Returns:
 *  - `0` for Polymarket (PM- prefix) — Polymarket has no taker fee in our model.
 *  - The effective rate (already × multiplier) for known Kalshi series.
 *  - `null` when fees haven't loaded yet, the series is unknown, or the
 *    series uses `flat` (which doesn't fit the `× P × (1−P)` formula).
 */
export function takerRateForTicker(ticker: string | null | undefined): number | null {
  if (!ticker) return null;
  if (ticker.startsWith('PM-')) return 0;
  const entry = feeFor(ticker);
  if (!entry) return null;
  if (entry.feeType === 'flat') return null;
  return entry.feeTakerRate ?? null;
}

/**
 * Look up the per-series maker rate for a Kalshi ticker.
 *
 * Returns:
 *  - `0` for Polymarket and for Kalshi series with `feeType=quadratic`
 *    (makers don't pay on plain quadratic schedules).
 *  - The effective rate for `quadratic_with_maker_fees` series (~0.0175 × multiplier).
 *  - `null` when fees haven't loaded yet, the series is unknown, or the
 *    series uses `flat`.
 */
export function makerRateForTicker(ticker: string | null | undefined): number | null {
  if (!ticker) return null;
  if (ticker.startsWith('PM-')) return 0;
  const entry = feeFor(ticker);
  if (!entry) return null;
  if (entry.feeType === 'flat') return null;
  return entry.feeMakerRate ?? 0;
}

export function getEffectivePriceFromBid(args: {
  bidCents: number | null;
  isTakerMode: boolean;
  takerFeeRate: number;
}): number | null {
  const { bidCents, isTakerMode, takerFeeRate } = args;
  if (bidCents === null) return null;
  if (!isTakerMode) return bidCents;
  const ask = bidToAsk(bidCents);
  return applyTakerFee(ask, takerFeeRate);
}

export interface TopResult {
  priceCents: number | null;
  liq: number | null;
  rawBidPriceCents: number | null;
}

function bestBid(levels: BookLevel[] | null | undefined): BookLevel | null {
  if (!Array.isArray(levels) || levels.length === 0) return null;
  let best = levels[0];
  for (const lvl of levels) {
    if (lvl && Number(lvl.priceCents) > Number(best.priceCents)) best = lvl;
  }
  return best ?? null;
}

export function topNoFromLevels(args: {
  noLevels: BookLevel[] | null | undefined;
  yesLevels: BookLevel[] | null | undefined;
  isTakerMode: boolean;
  takerFeeRate: number;
}): TopResult {
  const { noLevels, yesLevels, isTakerMode, takerFeeRate } = args;

  if (!Array.isArray(noLevels) || noLevels.length === 0) {
    return { priceCents: null, liq: null, rawBidPriceCents: null };
  }

  if (isTakerMode) {
    if (!Array.isArray(yesLevels) || yesLevels.length === 0) {
      return { priceCents: null, liq: null, rawBidPriceCents: null };
    }
    const bestYes = bestBid(yesLevels);
    if (!bestYes) return { priceCents: null, liq: null, rawBidPriceCents: null };
    const noAsk = bidToAsk(bestYes.priceCents);
    const effective = applyTakerFee(noAsk, takerFeeRate);
    return {
      priceCents: effective,
      liq: bestYes.size ?? null,
      rawBidPriceCents: bestYes.priceCents ?? null,
    };
  }

  const bestNo = bestBid(noLevels);
  if (!bestNo) return { priceCents: null, liq: null, rawBidPriceCents: null };
  const effective = getEffectivePriceFromBid({
    bidCents: bestNo.priceCents ?? null,
    isTakerMode,
    takerFeeRate,
  });
  return {
    priceCents: effective,
    liq: bestNo.size ?? null,
    rawBidPriceCents: bestNo.priceCents ?? null,
  };
}

export interface TeamWinData {
  priceCents: number | null;
  liq: number | null;
  rawBidPriceCents: number | null;
  marketTicker: string | null;
  tokenId?: string;
  noLevels: BookLevel[];
  yesLevels: BookLevel[];
}

/**
 * Resolve top-of-book pricing for a team side with fee adjustment.
 *
 * The taker rate is looked up per-ticker via the fee cache. If the cache
 * isn't ready or the series is unknown, fee adjustment is skipped (rate=0)
 * and the caller can decide whether to flag the row as "fee unknown".
 */
export function getTeamWinData(args: {
  game: VenueGameBooks | null | undefined;
  teamSide: TeamSide;
  isTakerMode: boolean;
}): TeamWinData {
  const { game, teamSide, isTakerMode } = args;
  if (!game) {
    return {
      priceCents: null,
      liq: null,
      rawBidPriceCents: null,
      marketTicker: null,
      noLevels: [],
      yesLevels: [],
    };
  }

  const opp = teamSide === 'away' ? game.markets.home : game.markets.away;
  const noLevels = opp?.no ?? [];
  const yesLevels = opp?.yes ?? [];
  const lookupTicker = opp?.marketTicker ?? null;
  // Fees not loaded / unknown series → rate=0 (no fee adjustment). Treat
  // the displayed price as raw rather than synthesizing a wrong fee.
  const takerFeeRate = takerRateForTicker(lookupTicker) ?? 0;
  const top = topNoFromLevels({
    noLevels,
    yesLevels,
    isTakerMode,
    takerFeeRate,
  });

  return {
    priceCents: top.priceCents,
    liq: top.liq,
    rawBidPriceCents: top.rawBidPriceCents,
    marketTicker: opp?.marketTicker ?? null,
    tokenId: opp?.tokenId,
    noLevels,
    yesLevels,
  };
}

export function next3NoLadders(
  noLevels: BookLevel[],
  bestBidPriceCents: number | null
): BookLevel[] {
  if (!Array.isArray(noLevels) || noLevels.length === 0) return [];
  if (bestBidPriceCents === null) return [];
  const below = noLevels.filter((lvl) => Number(lvl.priceCents) <= bestBidPriceCents);
  below.sort((a, b) => Number(b.priceCents) - Number(a.priceCents));
  return below.slice(0, 3);
}

export function next3YesLadders(
  yesLevels: BookLevel[],
  bestBidPriceCents: number | null
): BookLevel[] {
  if (!Array.isArray(yesLevels) || yesLevels.length === 0) return [];
  if (bestBidPriceCents === null) return [];
  const below = yesLevels.filter((lvl) => Number(lvl.priceCents) <= bestBidPriceCents);
  below.sort((a, b) => Number(b.priceCents) - Number(a.priceCents));
  return below.slice(0, 3);
}

export function liquidityDollars(
  liqContracts: number | null,
  priceCents: number | null
): number | null {
  if (liqContracts === null || priceCents === null) return null;
  const dollars = (Number(liqContracts) * Number(priceCents)) / 100;
  return Number.isFinite(dollars) ? dollars : null;
}

export function computeOrderPriceFromRawBid(
  rawBidPriceCents: number | null,
  isTakerMode: boolean
): number | null {
  if (rawBidPriceCents === null) return null;
  if (!isTakerMode) return rawBidPriceCents;
  return bidToAsk(rawBidPriceCents);
}
